import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { Status as StatusInterface, ModalFilter, MultiMetricData, MultiMetricOptionsProps } from '../../../types';
import { ReduxStates } from '../../../redux/reduxTypes';
import { fetchMultiMetric, returnParams, parseMultiMetricData, parseExportData } from '../helpers';
import MultiMetricDropdown from './MultiMetricDropdown';
import Graph from './Graph/Graph';
import Export from '../../../components/Export';
import { useOktaAuth } from '@okta/okta-react';

interface Props {
  marketFilters: (ModalFilter | null)[];
  formattedDates: string;
  selectedCurrency: string | null;
}

const MultiMetric = ({ marketFilters, formattedDates, selectedCurrency }: Props): React.ReactElement => {
  const [multiMetricData, setMultiMetricData] = useState<Array<MultiMetricData> | null>(null);
  const [metrics, setMetrics] = useState<Array<MultiMetricData> | null>(null);
  const [visibleMetrics, setVisibleMetrics] = useState<Array<MultiMetricData> | null>(null);
  const [status, setStatus] = useState<StatusInterface[]>([]);
  const lineGraphEl: any = useRef(null);
  const [fetching, setFetching] = useState<boolean>(true);
  const parentRef: any = useRef(null);
  const [showExportOptions, setShowExportOptions] = useState<boolean>(false);
  const [exportData, setExportData] = useState<Array<any> | null>(null);

  const copyData = useSelector((state: ReduxStates) => state.copyData);
  const client = useSelector((state: ReduxStates) => state.client);
  const { oktaAuth } = useOktaAuth();

  const fetchMultiMetricData = async (params: any) => {
    try {
      const mmData = await fetchMultiMetric(params, oktaAuth);
      setMultiMetricData(parseMultiMetricData(mmData?.metrics));
      setFetching(false);
    } catch (error: any) {
      setStatus([...status, { state: 'error', message: error }]);
      setFetching(false);
    }
  };

  useEffect(() => {
    //FETCH MULTI METRIC
    if (formattedDates) {
      resetMultiMetricData();
      setFetching(true);
      if (selectedCurrency && marketFilters.length) {
        const params = returnParams(marketFilters, formattedDates);
        fetchMultiMetricData(params);
      }
    }
  }, [marketFilters, formattedDates, selectedCurrency]);

  const resetMultiMetricData = () => {
    setMultiMetricData(null);
    setMetrics(null);
    setVisibleMetrics(null);
  };

  useEffect(() => {
    if (multiMetricData) {
      const metricsData = [...multiMetricData].map((metric) => {
        if (client === 'demo' && metric.dimension === 'spgr') {
          metric.valuesWithDates = [
            {
              date: '2022-03-01',
              value: 8.87
            },
            {
              date: '2022-04-01',
              value: 8.21
            },
            {
              date: '2022-05-01',
              value: 7.73
            }
          ];
        }
        return {
          ...metric,
          visible: true
        };
      });
      setMetrics(metricsData);
    }
  }, [multiMetricData]);

  useEffect(() => {
    if (metrics) {
      const visible = [...metrics].filter((metric) => metric.visible);
      setVisibleMetrics(visible);
    }
  }, [metrics]);

  useEffect(() => {
    if (visibleMetrics && visibleMetrics.length && selectedCurrency) {
      const exportData = parseExportData(visibleMetrics, selectedCurrency);
      setExportData(exportData);
    }
  }, [visibleMetrics]);

  const updateVisibility = (options: MultiMetricOptionsProps[]) => {
    if (metrics) {
      const newArr = [...metrics]; // copying the old datas array
      options.forEach((option, i) => {
        newArr[i].visible = option.visible;
        newArr[i].graphVisible = option.visible;
      });
      setMetrics(newArr);
    }
  };

  const updateGraphVisibility = (visible: boolean, id: string) => {
    if (visibleMetrics) {
      const newArr = [...visibleMetrics]; // copying the old datas array
      const dataDimension = newArr.find((dimension) => dimension?.dimension === id);
      if (dataDimension) dataDimension.graphVisible = visible;
      setVisibleMetrics(newArr);
    }
  };

  return fetching ? (
    <div className="w-full h-full min-h-[200px] flex items-center justify-center">
      <p className="font-nunitoRegular text-filterList text-base">{copyData?.loading}</p>
    </div>
  ) : (
    <div>
      {metrics && (
        <div id="multimetric_wrapper" className="p-4 bg-white" ref={parentRef}>
          <div className="relative flex justify-between pt-[18px] pr-[24px]">
            <MultiMetricDropdown
              data={multiMetricData}
              scrollable={multiMetricData && multiMetricData?.length > 8 ? true : false}
              onUpdate={(options) => updateVisibility(options)}
            />
            {visibleMetrics && visibleMetrics.find((x) => x.valuesWithDates.length > 0) && (
              <div className="absolute right-[10px] top-[2px]">
                <Export
                  graph={parentRef}
                  data={exportData}
                  fileName={'multimetric'}
                  showExportOptions={showExportOptions}
                  setShowExportOptions={setShowExportOptions}
                  automationID="multimetric_export_button"
                />
              </div>
            )}
          </div>
          {visibleMetrics && (
            <div className="mt-[54px]">
              <Graph
                graphData={visibleMetrics}
                lineGraphEl={lineGraphEl}
                selectedCurrency={selectedCurrency}
                updateGraphVisibility={updateGraphVisibility}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiMetric;
